import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { FolderTreeModalComponent } from 'src/app/components/modal/folder-tree-modal/folder-tree-modal.component';
import { FolderHierarchy } from 'src/app/models/folder-hierarchy.model';
import { SearchRequestBody } from 'src/app/models/response.model';
import { CommonService } from 'src/app/services/common.service';
import { SearchService } from 'src/app/services/search.service';

import { contentTypeSelection, formDefaultValues, ownerOptions, searchCriteriaInitial } from './variables';

type ByteUnit = 'KB' | 'MB' | 'GB';

const units: Record<ByteUnit, number> = {
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
};

@Component({
  selector: 'tbtk-detail-search',
  templateUrl: './detail-search.component.html',
  styleUrls: ['./detail-search.component.scss'],
  imports: [CommonModule, FolderTreeModalComponent, FormsModule, ReactiveFormsModule, TranslateModule],
  providers: [DatePipe],
  standalone: true,
})
export class DetailSearchComponent implements OnDestroy {
  searchFolderSubscription = new Subscription();

  detailSearchForm: FormGroup = new FormGroup({});
  detailSearchRequest: SearchRequestBody = { ...searchCriteriaInitial };
  options: string[] = ['KB', 'MB', 'GB'];
  maxDate!: string;
  el = document.getElementById('navbar-content');

  isFormValid = false;
  isSubmitted = false;

  contentTypeSelection = contentTypeSelection;
  ownerOptions = ownerOptions;

  get f(): { [key: string]: AbstractControl } {
    return this.detailSearchForm.controls;
  }

  constructor(public commonService: CommonService, private datePipe: DatePipe, private formBuilder: FormBuilder, private router: Router, public searchService: SearchService) {
    this.searchFolderSubscription.add(
      this.searchService.selectedFolder$.subscribe((value) => {
        this.getFolderId(value);
      }),
    );
    this.searchFolderSubscription.add(
      this.searchService.resetSearchForm$.subscribe(() => {
        this.resetForm(true);
      }),
    );
    const transformedDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    this.maxDate = transformedDate ?? '';
    this.createForm();
  }

  ngOnDestroy(): void {
    if (this.searchFolderSubscription) {
      this.searchFolderSubscription.unsubscribe();
    }
  }

  createForm(): void {
    this.detailSearchForm = this.formBuilder.group(formDefaultValues);
  }

  onSubmit(): void {
    this.isSubmitted = true;
    if (!this.detailSearchForm.invalid) {
      this.router.navigateByUrl('/depom');
      const minSize = this.f.minSize.value ? this.convertBytes(this.f.minSize.value, this.f.minSizeOptions.value) : null;
      const maxSize = this.f.maxSize.value ? this.convertBytes(this.f.maxSize.value, this.f.maxSizeOptions.value) : null;
      if (minSize && maxSize && minSize > maxSize) {
        this.isFormValid = false;
      } else {
        this.isFormValid = true;
        this.setFormValues();
        this.searchService.setIsDetailSearching(true);
        this.searchService.setSearchCriteria(this.detailSearchRequest);
        this.commonService.rebindListener.next();
        this.el?.click();
      }
    }
  }

  setFormValues(): void {
    const formValues = this.detailSearchForm.value;

    for (const key in formValues) {
      if (key in this.detailSearchRequest) {
        (this.detailSearchRequest as any)[key] = formValues[key];
      }
    }
    if (this.f.location.value === 'FAVORITED' || this.f.location.value === 'TRASHED') {
      this.detailSearchRequest.parent = null;
      this.f.parent.setValue(null);
    }
    this.detailSearchRequest.beforeDate = this.f.beforeDate.value ? this.f.beforeDate.value + 'T00:00:00Z' : null;
    this.detailSearchRequest.afterDate = this.f.afterDate.value ? this.f.afterDate.value + 'T00:00:00Z' : null;
    this.detailSearchRequest.contentType = this.f.contentType.value === 'OTHER' ? null : this.f.contentType.value;
    this.detailSearchRequest.favorited = this.f.location.value === 'FAVORITED' ? true : null;
    this.detailSearchRequest.trashed = this.f.location.value === 'TRASHED' ? true : null;
  }

  resetForm(noRebind?: boolean): void {
    this.detailSearchForm.patchValue(formDefaultValues);
    this.f.parentName.setValue(null);
    this.isSubmitted = false;
    this.isFormValid = false;
    this.detailSearchRequest = searchCriteriaInitial;
    this.searchService.setSearchCriteria(searchCriteriaInitial);
    this.searchService.setIsDetailSearching(false);
    if (!noRebind) {
      this.commonService.rebindListener.next();
    }
    this.el?.click();
  }

  getFolderId(folder: FolderHierarchy): void {
    this.detailSearchForm.patchValue({
      parent: folder.parent.objectId,
      parentName: folder.parent.name === 'root' ? 'Depom' : folder.parent.name,
    });
  }

  showModal(): void {
    this.searchService.setIsSearchDetail(true);
    this.commonService.setFolderTreeModalOpened(true);
    setTimeout(() => {
      this.commonService.showModal('#folderTreeModal');
      this.commonService.setIsEncrypted(false);
    }, 0);
  }

  onOwnerOptionsChanged(): void {
    if (this.f.ownerOptions.value === 'SPECIFIC_USERS_OBJECT') {
      this.f.email.setValidators(Validators.email);
    } else {
      this.f.email.clearValidators();
      this.f.email.setValue(null);
    }
  }

  convertBytes(value: string, option: string): number {
    return Number(value) * units[option as ByteUnit];
  }

  checkDate(): void {
    const beforeDateValue = this.f.beforeDate.value;
    const afterDateValue = this.f.afterDate.value;
    if (beforeDateValue && afterDateValue && beforeDateValue > afterDateValue) {
      this.f.afterDate.setValue(null);
    }
  }

  maxSizeChanged(event: any): void {
    this.isSubmitted = false;
    this.isFormValid = false;
    this.f.maxSize.patchValue(event.target.value.replace(/[^0-9]/g, ''));
  }

  minSizeChanged(event: any): void {
    this.f.minSize.patchValue(event.target.value.replace(/[^0-9]/g, ''));
  }
}
